/* eslint-disable camelcase, react-hooks/rules-of-hooks */
import { useQuery, UseQueryResult, useMutation } from 'react-query';
import axios from '@utils/axios';
import { BrowseNodeCategory } from '@api/database/models/browseNodeCategories';
import queryClient from '../queryClient';
import { DealPostType } from '../types';
import { useSnackbar } from './useSnackbar';

interface UnmappedBrowseNodeCategory {
  contextFreeName: string;
  categoryId: string;
  numItems: number;
}

export function useGetUnmappedBrowseNodeCategories(): UseQueryResult<
  UnmappedBrowseNodeCategory[]
> {
  const queryFn = async (): Promise<UnmappedBrowseNodeCategory[]> => {
    const result = await axios.get<{ data: UnmappedBrowseNodeCategory[] }>(
      `/api/dashboard/get-all-unmapped-browse-node-categories`
    );
    return result.data;
  };

  return useQuery<UnmappedBrowseNodeCategory[], Error>(
    `unmapped-browse-nodes`,
    () => queryFn(),
    {
      onError: () => {
        console.error(
          'Something went wrong while fetching the browse node categories'
        );
      }
    }
  );
}

export function useGetDealsByUnmappedCategory(
  categoryId: string
): UseQueryResult<DealPostType[]> {
  const queryFn = async (categoryId: string): Promise<DealPostType[]> => {
    const result = await axios.get<{ data: DealPostType[] }>(
      `/api/dashboard/browse-node-categories/get-all-deals-by-category/${categoryId}`
    );
    return result.data;
  };

  return useQuery<DealPostType[], Error>(
    `unmapped-deals-${categoryId}`,
    () => queryFn(categoryId),
    {
      onError: () => {
        console.error(
          'Something went wrong while fetching the browse node categories'
        );
      }
    }
  );
}

interface ASINBrowseNodeCategory {
  contextFreeName: string;
  categoryId: string;
  ASIN: string;
}

const queryASINUnmapped = async (
  ASIN: string
): Promise<ASINBrowseNodeCategory[]> => {
  const result = await axios.get<{ data: ASINBrowseNodeCategory[] }>(
    `/api/dashboard/get-ASIN-browse-Nodes?ASIN=${ASIN}`
  );
  return result.data;
};

export function useGetASINUnmapped(
  ASIN: string
): UseQueryResult<ASINBrowseNodeCategory[]> {
  return useQuery<ASINBrowseNodeCategory[], Error>(
    `ASIN-unmapped-${ASIN}`,
    () => queryASINUnmapped(ASIN),
    {
      onError: () => {
        console.error(
          'Something went wrong while fetching the browse node categories'
        );
      }
    }
  );
}

export function getUnMappedDeals(): UseQueryResult<DealPostType[]> {
  const queryFn = async (): Promise<DealPostType[]> => {
    const result = await axios.get<{ data: DealPostType[] }>(
      `/api/dashboard/get-unmapped-deals`
    );
    return result.data;
  };

  return useQuery<DealPostType[], Error>(
    `unmapped-deals-for-ui`,
    () => queryFn(),
    {
      onError: () => {
        console.error('Something went wrong while fetching the deals');
      }
    }
  );
}

export function useGetCategoriesByBrowseNodeId(
  dealListId: number
): UseQueryResult<BrowseNodeCategory[]> {
  const queryFn = async (dealListId: number): Promise<BrowseNodeCategory[]> => {
    const result = await axios.get<{ data: BrowseNodeCategory[] }>(
      `/api/dashboard/browse-node-categories/get-categories-by-deal-list-id/${dealListId}`
    );
    return result.data;
  };

  return useQuery<BrowseNodeCategory[], Error>(
    `categories-deal-list-${dealListId}`,
    () => queryFn(dealListId),
    {
      onError: () => {
        console.error('Something went wrong while fetching the deals');
      }
    }
  );
}

interface RemoveRequestData {
  categoryId: string;
  dealListId: number;
}

export function useRemoveBrowseNodeCategoryDealListMapping() {
  const { showMessage } = useSnackbar();

  return useMutation(
    async ({ categoryId, dealListId }: RemoveRequestData) => {
      await axios.post(
        `/api/dashboard/browse-node-categories/remove-category-deal-list`,
        {
          categoryId,
          dealListId
        }
      );
    },
    {
      onSuccess: (res, req) => {
        queryClient.refetchQueries(`unmapped-browse-nodes`);
        queryClient.refetchQueries(`unmapped-deals-for-ui`);
        queryClient.refetchQueries(`categories-deal-list-${req.dealListId}`);
        showMessage('Removed category from deal list mapping');
      }
    }
  );
}

interface RequestData {
  categoryId: string;
  dealListIds: number[];
  contextFreeName: string;
  ASIN?: string;
}

const browseNodeMutationFn = async ({
  contextFreeName,
  dealListIds,
  categoryId
}: RequestData): Promise<{
  added: number | null;
}> => {
  return axios.post(`/api/dashboard/add-browse-node-category-deal-list`, {
    contextFreeName,
    dealListIds,
    categoryId
  });
};

export function useAddBrowseNodeDealListMapping() {
  const { showMessage } = useSnackbar();

  return useMutation(browseNodeMutationFn, {
    onSuccess: ({ added }, { ASIN }) => {
      queryClient.refetchQueries(`unmapped-browse-nodes`);
      queryClient.refetchQueries(`unmapped-deals-for-ui`);
      if (ASIN) {
        queryClient.refetchQueries(`ASIN-unmapped-${ASIN}`);
      }
      if (added) {
        showMessage(`Added ${added} new ${added === 1 ? 'deal' : 'deals'}`);
      }
    }
  });
}
